import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PhoneCall, AlertTriangle, Clock, CalendarClock, Flame, X, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";
import { eur, STATUT_DEVIS } from "../../lib/format";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const BUCKETS = [
  { key: "aujourdhui", label: "À faire aujourd'hui", icon: PhoneCall, color: "#F59E0B" },
  { key: "en_retard", label: "En retard", icon: AlertTriangle, color: "#EF4444" },
  { key: "cette_semaine", label: "Cette semaine", icon: Clock, color: "#38BDF8" },
  { key: "a_venir", label: "À venir", icon: CalendarClock, color: "#94A3B8" },
];
const PRIO = { HAUTE: "bg-red-500/10 text-red-400 border-red-500/20", MOYENNE: "bg-amber-500/10 text-amber-400 border-amber-500/20", BASSE: "bg-slate-500/10 text-slate-300 border-slate-500/30" };
const RES_LABEL = { sans_reponse: "Sans réponse", interesse: "Intéressé", a_reflechir: "À réfléchir", refus: "Refus", valide: "Validé", autre: "Autre" };
const ALL = "__all__";

export default function ActionsView() {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ famille: ALL, statut: ALL, type_client: ALL, type_vente: ALL, montant: "" });
  const [searchParams, setSearchParams] = useSearchParams();
  const [lateOnly, setLateOnly] = useState(searchParams.get("focus") === "retard");

  useEffect(() => {
    const load = () => api.get("/actions-commerciales").then((r) => setData(r.data)).catch(() => {});
    load();
    window.addEventListener("crm:data-changed", load);
    return () => window.removeEventListener("crm:data-changed", load);
  }, []);
  useEffect(() => { setLateOnly(searchParams.get("focus") === "retard"); }, [searchParams]);

  const clearFocus = () => {
    setLateOnly(false);
    searchParams.delete("focus");
    setSearchParams(searchParams, { replace: true });
  };

  const completeTask = async (i) => {
    try {
      const task = { client_id: i.client_id, project_id: i.project_id, devis_id: i.id, type: "relance", titre: `Relance — ${i.client_nom || "client"}`, description: `Relance effectuée pour ${i.numero || i.reference_interne || "le dossier"}.` };
      const created = await api.post("/taches", task);
      await api.post(`/taches/${created.data.id}/complete`);
      window.dispatchEvent(new CustomEvent("crm:data-changed"));
    } catch (e) { console.error(e); }
  };

  const items = data?.items || [];
  const opts = useMemo(() => {
    const uniq = (k) => [...new Set(items.map((i) => i[k]).filter(Boolean))].sort();
    return { famille: uniq("famille"), statut: uniq("statut"), type_client: uniq("type_client"), type_vente: uniq("type_vente") };
  }, [items]);

  const filtered = items.filter((i) =>
    (f.famille === ALL || i.famille === f.famille) &&
    (f.statut === ALL || i.statut === f.statut) &&
    (f.type_client === ALL || i.type_client === f.type_client) &&
    (f.type_vente === ALL || i.type_vente === f.type_vente) &&
    (!f.montant || (i.montant_total || 0) >= Number(f.montant))
  );

  if (!data) return <div className="text-slate-500">Chargement des actions…</div>;

  const Filter = ({ k, label, list }) => (
    <Select value={f[k]} onValueChange={(v) => setF({ ...f, [k]: v })}>
      <SelectTrigger data-testid={`actions-filter-${k}`} className="w-full sm:w-44 bg-[#111827] border-[#1E293B]"><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
        <SelectItem value={ALL}>{label} (tous)</SelectItem>
        {list.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const visibleBuckets = lateOnly ? BUCKETS.filter((b) => b.key === "en_retard") : BUCKETS;
  const lateCount = items.filter((i) => i.bucket === "en_retard").length;

  return (
    <div className="space-y-6" data-testid="actions-view">
      <div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Actions Commerciales</h1>
        <p className="text-slate-400 text-sm mt-1">Qui dois-je appeler aujourd'hui ? — {data.total} dossiers ouverts · {data.sans_reponse} sans réponse.</p>
      </div>

      {lateOnly && (
        <div data-testid="actions-focus-retard" className="flex items-center justify-between gap-3 rounded-xl border border-red-500/25 bg-red-500/5 px-4 py-3">
          <span className="flex items-center gap-2 text-sm text-red-300 font-semibold">
            <AlertTriangle className="h-4 w-4" /> Relances en retard uniquement — {lateCount} dossier{lateCount > 1 ? "s" : ""}
          </span>
          <button data-testid="actions-clear-focus" onClick={clearFocus} className="flex items-center gap-1 text-xs text-slate-300 hover:text-white border border-[#1E293B] rounded-lg px-2.5 py-1 transition-colors">
            <X className="h-3.5 w-3.5" /> Tout afficher
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <Filter k="famille" label="Famille" list={opts.famille} />
        <Filter k="statut" label="Statut" list={opts.statut} />
        <Filter k="type_client" label="Type client" list={opts.type_client} />
        <Filter k="type_vente" label="Type vente" list={opts.type_vente} />
        <Input data-testid="actions-filter-montant" type="number" placeholder="Montant min €" value={f.montant} onChange={(e) => setF({ ...f, montant: e.target.value })} className="w-full sm:w-36 bg-[#111827] border-[#1E293B] font-mono-metric" />
      </div>

      <div className={`grid grid-cols-1 gap-4 ${lateOnly ? "" : "xl:grid-cols-2"}`}>
        {visibleBuckets.map((b) => {
          const list = filtered.filter((i) => i.bucket === b.key);
          const Icon = b.icon;
          return (
            <div key={b.key} className="rounded-xl bg-[#111827] border border-[#1E293B] overflow-hidden" data-testid={`actions-bucket-${b.key}`}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#1E293B]" style={{ borderLeft: `3px solid ${b.color}` }}>
                <span className="font-head font-bold flex items-center gap-2"><Icon className="h-4 w-4" style={{ color: b.color }} /> {b.label}</span>
                <span className="font-mono-metric text-sm text-slate-400">{list.length}</span>
              </div>
              <div className="max-h-[420px] overflow-y-auto divide-y divide-[#1E293B]/60">
                {list.length === 0 && <div className="p-5 text-sm text-slate-500">Rien à traiter.</div>}
                {list.map((i) => (
                  <div key={i.id} className="p-4 hover:bg-[#0B0F17]/50" data-testid={`action-row-${i.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{i.client_nom || "—"}
                          {i.sans_reponse && <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-400 border border-orange-500/20">Sans réponse</span>}
                        </div>
                        <div className="text-xs text-slate-500 font-mono-metric">{i.reference_interne || i.numero || "sans n°"} · {i.famille || "—"} · {i.type_vente || "—"}</div>
                      </div>
                      <span className={`shrink-0 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${PRIO[i.priorite]}`}>
                        {i.priorite === "HAUTE" && <Flame className="h-3 w-3" />}{i.priorite}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs">
                      <span className={`font-semibold px-2 py-0.5 rounded border ${STATUT_DEVIS[i.statut] || "bg-slate-500/10 text-slate-300 border-slate-500/30"}`}>{i.statut}</span>
                      <span className="font-mono-metric font-semibold text-slate-100">{eur(i.montant_total)}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                      <span>
                        Dernière action : {i.date_derniere_action || "—"}
                        {i.derniere_action_resultat && (
                          <span className="ml-1.5 text-[10px] font-bold uppercase px-1 py-0.5 rounded bg-slate-700/40 text-slate-300">{RES_LABEL[i.derniere_action_resultat] || i.derniere_action_resultat}</span>
                        )}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className={i.jours_retard > 0 ? "text-red-400" : ""}>
                          Relance : {i.date_prochaine_relance || "—"}
                          {i.source_prochaine === "journal" ? " (journal)" : i.relance_estimee ? " (est. +15j)" : ""}
                          {i.jours_retard > 0 ? ` · +${i.jours_retard}j` : ""}
                        </span>
                        {i.date_prochaine_relance && (
                          <button type="button" onClick={() => completeTask(i)} title="Marquer comme relancé" className="inline-flex items-center gap-1 rounded-md border border-emerald-500/20 bg-emerald-500/10 px-2 py-1 text-[10px] font-semibold text-emerald-300 hover:bg-emerald-500/20">
                            <CheckCircle2 className="h-3 w-3" /> Relancé
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
